// Renders a tour's ordered SLIDES (photos + quote slides) and lets the user
// REARRANGE and REMOVE them.
//
// Built for a non-technical user on an iPad: the primary reorder UI is a set of
// large, unambiguous BUTTONS (move to top / up / down) with a position indicator
// so the sequence is legible. As a touch-friendly bonus there's also drag-to-
// reorder via Pointer Events (no dnd library) on a dedicated drag handle, so it
// coexists with scrolling and the buttons. Reorder is synchronous from this
// component's view (the store updates order optimistically and persists it to
// DynamoDB — PUT /tours/{slug} — in the background), so it is NOT a "busy"
// operation; we just recompute the full id sequence and hand it to onReorder.
//
// A slide is either a PHOTO (an artifact, removed by dropping the tour slug from its
// tourTags — the artifact is NOT deleted) or a QUOTE slide (removed from the tour
// record's quoteSlides). Both removals are async, so we disable the row's controls
// while one resolves, respect the `busy` flag, and surface a lightweight inline
// error if it rejects. The parent dispatches the right removal by `slide.kind`.

import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { PointerEvent as ReactPointerEvent } from "react";
import { imageUrl } from "../config";
import { artifactLabel, quoteSlideLabel, type TourSlide } from "../tours";
import { ArtImage } from "./ArtImage";
import "./TourDetailGrid.css";

export interface TourDetailGridProps {
  slides: TourSlide[];
  onReorder: (orderedIds: string[]) => void;
  onRemove: (slide: TourSlide) => Promise<void>;
  busy?: boolean;
}

/** Move the item at `from` to `to` in a fresh copy of `ids` (no mutation). */
function moveId(ids: string[], from: number, to: number): string[] {
  if (from === to || from < 0 || to < 0 || from >= ids.length || to >= ids.length) {
    return ids;
  }
  const next = ids.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** A short, human label for a slide (used in aria-labels and the drag ghost). */
function slideLabel(s: TourSlide): string {
  return s.kind === "photo" ? artifactLabel(s.artifact) : quoteSlideLabel(s.slide);
}

export function TourDetailGrid({ slides, onReorder, onRemove, busy }: TourDetailGridProps) {
  // Ids whose own remove() is in flight — disables just that row's controls.
  const [removing, setRemoving] = useState<Set<string>>(new Set());
  // Per-row inline error (e.g. a failed remove), keyed by slide id.
  const [errors, setErrors] = useState<Record<string, string>>({});

  // --- Drag-to-reorder (Pointer Events; iPad-friendly, no library) ----------
  // We only track an index pair: the row being dragged and the row it currently
  // hovers over. The committed list still lives in `slides` (parent-owned); we
  // commit a real reorder on pointer-up. `dragId` doubles as the "is dragging"
  // flag for styling.
  const [dragId, setDragId] = useState<string | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  // Refs so the pointermove/up handlers (bound imperatively) read live values
  // without re-binding on every render.
  const listRef = useRef<HTMLOListElement>(null);
  const dragIndexRef = useRef<number | null>(null);
  const overIndexRef = useRef<number | null>(null);
  // A floating, translucent preview of the row being dragged that tracks the
  // pointer — clear "you're holding this" feedback (esp. on touch, where the
  // finger hides the handle). `left`/`width` are measured once at grab time;
  // `top` follows the pointer. `grabOffsetY` keeps the ghost aligned to where it
  // was grabbed instead of snapping its top edge to the finger.
  const [ghost, setGhost] = useState<{ left: number; top: number; width: number } | null>(null);
  const grabOffsetYRef = useRef(0);

  if (slides.length === 0) {
    return (
      <div className="empty-state">
        No slides in this tour yet — tap “Add artworks” or “New quote slide” to add some.
      </div>
    );
  }

  const ids = slides.map((s) => s.id);

  // Apply a move by index and persist the full new order. No-op moves are
  // filtered by moveId so we never churn the parent/localStorage needlessly.
  const commitMove = (from: number, to: number) => {
    const next = moveId(ids, from, to);
    if (next !== ids) onReorder(next);
  };

  const handleRemove = async (slide: TourSlide) => {
    const id = slide.id;
    // Clear any stale error for this row and mark it pending.
    setErrors((e) => {
      if (!(id in e)) return e;
      const next = { ...e };
      delete next[id];
      return next;
    });
    setRemoving((s) => new Set(s).add(id));
    try {
      await onRemove(slide);
      // On success the parent refreshes and this row disappears from `slides`;
      // no further state cleanup is needed for it.
    } catch {
      setErrors((e) => ({
        ...e,
        [id]: "Couldn’t remove this from the tour. Tap to try again.",
      }));
    } finally {
      setRemoving((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  // --- Drag handlers --------------------------------------------------------
  // Find which row index a clientY falls over, by measuring the rendered rows.
  const indexAtY = (clientY: number): number | null => {
    const list = listRef.current;
    if (!list) return null;
    const rows = list.querySelectorAll<HTMLElement>("[data-row]");
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i].getBoundingClientRect();
      // Use each row's vertical midpoint so the insertion point flips halfway.
      if (clientY < r.top + r.height / 2) return i;
    }
    return rows.length - 1;
  };

  const onHandlePointerDown = (e: ReactPointerEvent, index: number, id: string) => {
    // Left button / touch / pen only; ignore if a mutation is in flight.
    if (busy || e.button !== 0) return;
    e.preventDefault();
    dragIndexRef.current = index;
    setDragId(id);
    setOverIndex(index);

    const handleEl = e.currentTarget as HTMLElement;
    // Measure the row so the floating ghost matches its size and tracks the
    // pointer from wherever along its height it was grabbed.
    const rowEl = handleEl.closest<HTMLElement>("[data-row]");
    if (rowEl) {
      const rect = rowEl.getBoundingClientRect();
      grabOffsetYRef.current = e.clientY - rect.top;
      setGhost({ left: rect.left, top: rect.top, width: rect.width });
    }

    try {
      handleEl.setPointerCapture(e.pointerId);
    } catch {
      // setPointerCapture can throw if the pointer is already gone; harmless.
    }

    const onMove = (ev: PointerEvent) => {
      const idx = indexAtY(ev.clientY);
      if (idx !== null) setOverIndex(idx);
      // Glide the ghost vertically with the pointer (left/width stay fixed).
      setGhost((g) => (g ? { ...g, top: ev.clientY - grabOffsetYRef.current } : g));
    };
    const onUp = () => {
      const from = dragIndexRef.current;
      const to = overIndexRef.current;
      if (from !== null && to !== null) commitMove(from, to);
      dragIndexRef.current = null;
      setDragId(null);
      setOverIndex(null);
      setGhost(null);
      handleEl.removeEventListener("pointermove", onMove);
      handleEl.removeEventListener("pointerup", onUp);
      handleEl.removeEventListener("pointercancel", onUp);
    };
    handleEl.addEventListener("pointermove", onMove);
    handleEl.addEventListener("pointerup", onUp);
    handleEl.addEventListener("pointercancel", onUp);
  };

  // Mirror overIndex into a ref so the pointerup closure reads the latest drop
  // target without re-binding listeners on every move.
  overIndexRef.current = overIndex;

  // The slide currently being dragged — rendered as the floating ghost preview.
  const draggedSlide = dragId !== null ? slides.find((s) => s.id === dragId) ?? null : null;

  return (
    <ol className="tour-detail-grid" ref={listRef}>
      {slides.map((slide, index) => {
        const id = slide.id;
        const isFirst = index === 0;
        const isLast = index === slides.length - 1;
        const rowPending = removing.has(id);
        // Disable this row's controls while its own remove resolves, while any
        // membership mutation is in flight, or while a drag is active.
        const rowBusy = !!busy || rowPending || dragId !== null;
        const error = errors[id];
        const label = slideLabel(slide);

        const isDragged = dragId === id;
        // While dragging, mark the row the pointer is hovering as the drop target.
        const isDropTarget = dragId !== null && !isDragged && overIndex === index;

        const rowClass = [
          "tdg-row",
          slide.kind === "quote" ? "tdg-row--quote" : "",
          isDragged ? "tdg-row--dragging" : "",
          isDropTarget ? "tdg-row--drop" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <li key={id} className={rowClass} data-row>
            {/* Drag handle (bonus): touch-drag to reorder on iPad. Buttons below
                are the reliable, primary path. */}
            <button
              type="button"
              className="tdg-row__handle"
              aria-label={`Drag to reorder ${label}`}
              disabled={!!busy || rowPending}
              onPointerDown={(e) => onHandlePointerDown(e, index, id)}
            >
              <span aria-hidden="true">⠿</span>
            </button>

            {slide.kind === "photo" ? (
              <div className="tdg-row__cover">
                <ArtImage
                  src={
                    imageUrl(slide.artifact.thumbnailKey) ??
                    imageUrl(slide.artifact.canonicalImageKey)
                  }
                  fallbackSrc={imageUrl(slide.artifact.originalImageKey)}
                  alt={label}
                  className="tdg-row__img"
                />
              </div>
            ) : (
              <div className="tdg-row__cover tdg-row__cover--quote" aria-hidden="true">
                <span className="tdg-row__quote-mark">&ldquo;</span>
              </div>
            )}

            <div className="tdg-row__meta">
              <span className="tdg-row__pos">
                {index + 1} / {slides.length}
              </span>
              <span className="tdg-row__label">{label}</span>
              {slide.kind === "photo"
                ? slide.artifact.artist &&
                  slide.artifact.artist.trim() && (
                    <span className="tdg-row__artist">{slide.artifact.artist.trim()}</span>
                  )
                : (
                    <span className="tdg-row__kind">
                      Quote slide · {slide.slide.quotes.length}{" "}
                      {slide.slide.quotes.length === 1 ? "quote" : "quotes"}
                    </span>
                  )}
              {error && (
                <button
                  type="button"
                  className="tdg-row__error"
                  onClick={() => handleRemove(slide)}
                >
                  {error}
                </button>
              )}
            </div>

            <div className="tdg-row__actions">
              <div className="tdg-row__move">
                <button
                  type="button"
                  className="btn btn--small tdg-row__btn"
                  disabled={rowBusy || isFirst}
                  aria-label={`Move ${label} to top`}
                  onClick={() => commitMove(index, 0)}
                >
                  ⤒ Top
                </button>
                <button
                  type="button"
                  className="btn btn--small tdg-row__btn"
                  disabled={rowBusy || isFirst}
                  aria-label={`Move ${label} up`}
                  onClick={() => commitMove(index, index - 1)}
                >
                  ↑ Up
                </button>
                <button
                  type="button"
                  className="btn btn--small tdg-row__btn"
                  disabled={rowBusy || isLast}
                  aria-label={`Move ${label} down`}
                  onClick={() => commitMove(index, index + 1)}
                >
                  ↓ Down
                </button>
              </div>
              <button
                type="button"
                className="btn btn--small btn--danger tdg-row__remove"
                disabled={rowBusy}
                aria-label={`Remove ${label} from this tour`}
                onClick={() => handleRemove(slide)}
              >
                {rowPending ? "Removing…" : "Remove from tour"}
              </button>
            </div>
          </li>
        );
      })}

      {/* Floating translucent preview of the dragged slide. Portaled to <body> so
          it floats above everything and isn't clipped by the scroll container;
          it's purely visual (pointer-events: none) and tracks the pointer. */}
      {ghost &&
        draggedSlide &&
        createPortal(
          <div
            className="tdg-ghost"
            style={{ left: ghost.left, top: ghost.top, width: ghost.width }}
            aria-hidden="true"
          >
            {draggedSlide.kind === "photo" ? (
              <div className="tdg-ghost__cover">
                <ArtImage
                  src={
                    imageUrl(draggedSlide.artifact.thumbnailKey) ??
                    imageUrl(draggedSlide.artifact.canonicalImageKey)
                  }
                  fallbackSrc={imageUrl(draggedSlide.artifact.originalImageKey)}
                  alt=""
                  className="tdg-row__img"
                />
              </div>
            ) : (
              <div className="tdg-ghost__cover tdg-row__cover--quote">
                <span className="tdg-row__quote-mark">&ldquo;</span>
              </div>
            )}
            <span className="tdg-ghost__label">{slideLabel(draggedSlide)}</span>
          </div>,
          document.body,
        )}
    </ol>
  );
}
